"use client";

/**
 * UpdatingCapsule — the deploy transport capsule (the hand-off rail).
 *
 * One fixed top-center capsule on every (app) surface, including mid-turn. A
 * deploy is silent; only a hand-off whose link is still down after
 * HANDOVER_QUIET_MS shows calm gold ("Updating Vestra"), resolves to a
 * brief "Updated" tick when the successor connects, and past the TTL the
 * store degrades to the ordinary offline UX — this capsule simply unmounts,
 * so an update can never dress up a real outage. Mounted once in AppShell.
 * Visual canon: mockups/deploy-updating-state-2026-08-07.html.
 */

import { useConnectionStore } from "@/stores/connectionStore";
import { CircleCheckGoldIcon } from "@/components/icons/CircleCheckGoldIcon";

export function UpdatingCapsule() {
  const updating = useConnectionStore((s) => s.deployUpdating);
  const updatedTick = useConnectionStore((s) => s.deployUpdatedTick);

  if (!updating && !updatedTick) return null;

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 w-max max-w-[calc(100vw-1rem)] z-[85] pointer-events-none"
      data-testid="updating-capsule"
      data-state={updating ? "updating" : "updated"}
      role="status"
      aria-live="polite"
    >
      <div className="inline-flex items-center gap-2 rounded-full border border-border-default bg-bg-elevated px-4 py-1.5 text-3.5 font-medium text-fg-default shadow-connectivity-capsule">
        {updating ? (
          <>
            <span
              className="w-2 h-2 rounded-full shrink-0 bg-gold-molten-radial animate-deploy-capsule-breathe motion-reduce:animate-none"
              aria-hidden
            />
            <span>Updating Vestra</span>
            <span className="font-normal text-fg-subtle">back in a moment</span>
          </>
        ) : (
          <>
            <CircleCheckGoldIcon />
            <span>Updated</span>
          </>
        )}
      </div>
    </div>
  );
}
